import { Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useCart } from "../contexts/CartContext";
import { useSettings } from "../contexts/SettingsContext";
import { Minus, Plus, Trash2, ArrowRight, AlertTriangle, MapPin, Loader2 } from "lucide-react";
import { pushViewCart, pushRemoveFromCart } from "../lib/gtmDataLayer";
import { toast } from "sonner";
import api from "../lib/api";
import CartItemImage from "../components/CartItemImage";
import SEOHead from "../components/SEOHead";

const PINCODE_STORAGE_KEY = "sdf_delivery_pincode";

export default function Cart() {
  const { items, updateQty, remove, subtotal, count } = useCart();
  const { amountToFreeDelivery, freeDeliveryThreshold, freeDeliveryEnabled, handlingFee, activeCharges, chargesTotal, settings } = useSettings();
  const navigate = useNavigate();

  const minOrder = Number(settings?.min_order_amount || 200);

  // ── Dynamic Shiprocket shipping ─────────────────────────────────────────
  const [pincode, setPincode] = useState(() => {
    try { return localStorage.getItem(PINCODE_STORAGE_KEY) || ""; } catch { return ""; }
  });
  const [quote, setQuote] = useState(null);          // { rate, free, courier_name, etd, serviceable }
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState("");

  const validPincode = /^\d{6}$/.test(pincode);

  // iter57 — fire GA4 view_cart ONCE per mount when cart has items.
  const viewCartFiredRef = useRef(false);
  useEffect(() => {
    if (items.length === 0) return;
    if (viewCartFiredRef.current) return;
    viewCartFiredRef.current = true;
    pushViewCart(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const fetchQuote = async (pin) => {
    setQuoteError("");
    setQuoteLoading(true);
    try {
      const { data } = await api.post("/shipping/quote", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, variant_label: i.variant_label })),
        pincode: pin,
        cod: false,
      });
      setQuote(data);
      try { localStorage.setItem(PINCODE_STORAGE_KEY, pin); } catch { /* ignore */ }
    } catch (e) {
      setQuote(null);
      setQuoteError(e?.response?.data?.detail || "Could not fetch delivery rate");
    } finally {
      setQuoteLoading(false);
    }
  };

  // Auto-refresh when cart contents change AND a valid pincode is set.
  // `fetchQuote` is recreated every render but reading it via closure is
  // intentional (we don't want subtotal-driven re-fetches to be skipped).
  useEffect(() => {
    if (!validPincode || items.length === 0) return;
    fetchQuote(pincode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal, count, validPincode, pincode]);

  const delivery = quote ? Number(quote.rate || 0) : 0;
  // Only render the "Free" badge if both the toggle is ON and Shiprocket/threshold returned a free quote.
  const isFreeDelivery = freeDeliveryEnabled && !!quote?.free;
  // Session L / iter109 — Grand Total uses the dynamic charges total
  // when the admin has configured a Charges list, else falls back to
  // the legacy flat handling fee.
  const total = subtotal + delivery + (chargesTotal || handlingFee);
  const belowMin = subtotal < minOrder;
  const amountToMin = Math.max(0, minOrder - subtotal);
  const amountToFree = amountToFreeDelivery(subtotal);

  const handlePincodeSubmit = (e) => {
    e.preventDefault();
    if (!validPincode) { toast.error("Enter a valid 6-digit pincode"); return; }
    fetchQuote(pincode);
  };

  const handleCheckout = () => {
    if (belowMin) {
      toast.error(`MINIMUM ORDERING AMOUNT IS ₹${minOrder.toFixed(0)}`, { duration: 3500 });
      return;
    }
    navigate("/checkout");
  };

  if (items.length === 0) {
    return (
      <div className="container-x py-24 text-center" data-testid="empty-cart">
        <SEOHead title="Your Basket | Sri Durga Foods" description="Review the items in your Sri Durga Foods basket before checkout." noindex />
        <h1 className="font-serif text-5xl text-brand-dark mb-4">Your basket is empty</h1>
        <p className="text-brand-muted mb-8">Wander the shelves — there&apos;s a jar with your name on it.</p>
        <Link to="/shop" className="btn-primary" data-testid="empty-cart-shop-btn">Browse the pantry</Link>
      </div>
    );
  }

  return (
    <div className="container-x py-12 lg:py-20" data-testid="cart-page">
      <SEOHead title="Your Basket | Sri Durga Foods" description="Review the items in your Sri Durga Foods basket before checkout." noindex />
      <div className="mb-10">
        <span className="overline mb-3 block">Your Basket</span>
        <h1 className="font-serif text-4xl sm:text-5xl text-brand-dark">{count} {count === 1 ? "item" : "items"}</h1>
      </div>

      {/* Minimum ordering amount notification — red, full width */}
      {belowMin && (
        <div
          className="mb-6 bg-brand-chili text-brand-cream rounded-2xl px-5 py-4 flex items-center gap-3"
          data-testid="cart-min-order-notice"
        >
          <AlertTriangle className="w-5 h-5 shrink-0" strokeWidth={2} />
          <div className="text-sm font-semibold tracking-wide uppercase">
            Minimum ordering amount is ₹{minOrder.toFixed(0)}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8 space-y-4">
          {items.map((it) => (
            <div key={`${it.product_id}__${it.variant_label}`} className="flex items-center gap-4 sm:gap-6 bg-white border border-brand-line rounded-2xl p-4 sm:p-5" data-testid={`cart-item-${it.product_id}`}>
              <CartItemImage
                src={it.image_url}
                alt={it.name}
                className="w-24 h-24 sm:w-28 sm:h-28 object-cover rounded-xl bg-brand-cream flex-shrink-0"
                testid={`cart-item-image-${it.product_id}`}
              />
              <div className="flex-1 min-w-0">
                <h3 className="font-serif text-lg sm:text-xl text-brand-dark leading-tight">{it.name}</h3>
                {it.variant_label && <div className="text-xs text-brand-muted mt-0.5">{it.variant_label}</div>}
                <div className="text-sm text-brand-muted mt-1">₹{it.price.toFixed(0)}</div>
                <div className="inline-flex items-center border border-brand-line rounded-full overflow-hidden mt-3">
                  <button onClick={() => updateQty(it.product_id, it.variant_label, it.quantity - 1)} className="p-2 hover:bg-brand-subtle"><Minus className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                  <span className="px-4 text-sm font-medium">{it.quantity}</span>
                  <button onClick={() => updateQty(it.product_id, it.variant_label, it.quantity + 1)} className="p-2 hover:bg-brand-subtle"><Plus className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                </div>
              </div>
              <div className="text-right">
                <div className="font-semibold text-brand-dark">₹{(it.price * it.quantity).toFixed(0)}</div>
                <button onClick={() => { pushRemoveFromCart({ product_id: it.product_id, name: it.name, category: it.category }, { label: it.variant_label, price: it.price }, it.quantity); remove(it.product_id, it.variant_label); }} className="text-brand-muted hover:text-brand-chili mt-3" data-testid={`cart-remove-${it.product_id}`}>
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
          {/* iter53 — product disclaimer. Appears below the last product
              and above the Order Summary (which is the next column on
              desktop, stacked below on mobile). 9pt (iter54 — reduced from
              15pt to be subtle/informational), Sri Durga logo red, max 2
              lines. The empty-cart branch above returns early, so this
              block only ever renders when items.length > 0 — no blank
              space when the cart is empty. */}
          <p
            data-testid="cart-disclaimer"
            className="pt-2 leading-snug line-clamp-2"
            style={{ color: "#E11D24", fontSize: "9pt" }}
          >
            Disclaimer: Product images are for illustrative purposes only. Actual product appearance may vary slightly.
          </p>
        </div>

        <div className="lg:col-span-4">
          <div className="bg-white border border-brand-line rounded-2xl p-6 sticky top-28">
            <h3 className="font-serif text-2xl text-brand-dark mb-6">Order Summary</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-brand-muted">Product Total</span>
                <span className="font-medium" data-testid="summary-subtotal">₹{subtotal.toFixed(0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-brand-muted">Shipping Charge</span>
                <span className="font-medium" data-testid="summary-delivery">
                  {quoteLoading
                    ? "…"
                    : isFreeDelivery
                    ? "Free"
                    : quote
                    ? `₹${Math.round(delivery)}`
                    : "—"}
                </span>
              </div>
              {/* Session L / iter109 — dynamic Charges list.  Falls
                   back to the single "Handling Fee" row when the admin
                   hasn't opened the new Charges section yet. */}
              {activeCharges && activeCharges.length > 0 ? (
                activeCharges.map((c) => (
                  <div key={c.id} className="flex justify-between" data-testid={`summary-charge-${c.id}`}>
                    <span className="text-brand-muted">{c.name}</span>
                    <span className="font-medium">₹{Number(c.amount).toFixed(0)}</span>
                  </div>
                ))
              ) : (
                <div className="flex justify-between">
                  <span className="text-brand-muted">Handling Fee</span>
                  <span className="font-medium" data-testid="summary-handling">₹{Number(handlingFee).toFixed(0)}</span>
                </div>
              )}

              {/* Pincode check — live Shiprocket rate */}
              <form onSubmit={handlePincodeSubmit} className="pt-1" data-testid="cart-pincode-form">
                <label className="flex items-center gap-1.5 text-xs text-brand-muted mb-1.5">
                  <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Check delivery by pincode
                </label>
                <div className="flex items-stretch gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    placeholder="6-digit pincode"
                    value={pincode}
                    onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="flex-1 min-w-0 border border-brand-line rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-chili"
                    data-testid="cart-pincode-input"
                  />
                  <button
                    type="submit"
                    disabled={!validPincode || quoteLoading}
                    className="px-3 py-2 text-xs font-semibold uppercase tracking-wide bg-brand-dark text-brand-cream rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
                    data-testid="cart-pincode-check-btn"
                  >
                    {quoteLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Check"}
                  </button>
                </div>
                {quote && !quoteError && (
                  <div className="text-[11px] text-brand-muted mt-1.5" data-testid="cart-pincode-result">
                    {freeDeliveryEnabled && quote.free
                      ? "Free delivery on this order."
                      : `${quote.courier_name || "Courier"}${quote.etd ? ` · ETA ${quote.etd}` : ""}`}
                  </div>
                )}
                {quoteError && (
                  <div className="text-[11px] text-brand-chili mt-1.5" data-testid="cart-pincode-error">{quoteError}</div>
                )}
                {!quote && !quoteError && !quoteLoading && (
                  <div className="text-[11px] text-brand-muted mt-1.5" data-testid="cart-pincode-empty">
                    Enter your pincode to view live shipping cost.
                  </div>
                )}
              </form>

              {/* iter99 — Polite minimum-order nudge shown only when the
                  PRODUCT subtotal is below the configured minimum. */}
              {belowMin && (
                <div
                  className="text-xs text-brand-chili bg-brand-chili/10 border border-brand-chili/20 px-3 py-2.5 rounded-lg font-medium leading-relaxed"
                  data-testid="min-order-hint"
                >
                  Almost there! Add ₹{amountToMin.toFixed(0)} more to your basket to place your order.
                  Tap &lsquo;Browse the Pantry&rsquo; to continue shopping.
                </div>
              )}

              {/* Add ₹X more for free delivery — only when toggle is ON */}
              {freeDeliveryEnabled && amountToFree > 0 && subtotal >= minOrder && (
                <div className="text-xs text-brand-chili bg-brand-subtle px-3 py-2 rounded-lg" data-testid="free-delivery-hint">
                  Add ₹{amountToFree.toFixed(0)} more for free delivery
                </div>
              )}
              {/* When still below min, also surface the free-delivery nudge below — only when toggle is ON */}
              {freeDeliveryEnabled && belowMin && freeDeliveryThreshold > minOrder && (
                <div className="text-xs text-brand-muted bg-brand-subtle px-3 py-2 rounded-lg" data-testid="free-delivery-hint-below-min">
                  Add ₹{amountToFree.toFixed(0)} more for free delivery
                </div>
              )}
            </div>
            <div className="border-t border-brand-line mt-5 pt-5 flex justify-between items-baseline">
              <span className="font-serif text-2xl text-brand-dark">Grand Total</span>
              <span className="font-serif text-3xl text-brand-chili font-semibold" data-testid="summary-total">₹{total.toFixed(0)}</span>
            </div>
            {belowMin ? (
              <button
                onClick={() => navigate("/shop")}
                className="btn-primary w-full mt-6"
                data-testid="cart-browse-pantry-btn"
              >
                Browse the Pantry <ArrowRight className="ml-2 w-4 h-4" strokeWidth={2} />
              </button>
            ) : (
              <button
                onClick={handleCheckout}
                className="btn-primary w-full mt-6"
                data-testid="cart-checkout-btn"
              >
                Proceed to checkout <ArrowRight className="ml-2 w-4 h-4" strokeWidth={2} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
